<template>
  <a-form :form="form" @submit.prevent="onSubmit" layout="vertical">
    <a-form-item
      v-bind="errorsBind('non_field_errors')"
      class="non-fields-errors"
    />
    <a-form-item label="Kurs" v-bind="errorsBind('course')" required>
      <a-select
        v-model="form.course"
        :options="courses"
        :disabled="courses.length === 1"
        optionFilterProp="children"
        showSearch
        placeholder="Wybierz kurs"
      />
    </a-form-item>
    <a-form-item v-bind="errorsBind('timespan')">
      <a-row :gutter="16">
        <a-col :span="8">
          <a-form-item class="no-margin-and-padding" label="Data" required>
            <a-date-picker
              :disabled="!form.course || isFormDisabled"
              class="fluid"
              :allowClear="false"
              format="DD.MM.YYYY"
              :disabledDate="disabledDate"
              @change="dateChanged"
              v-model="form.selectedDate"
              placeholder="Dzień"
            />
          </a-form-item>
        </a-col>
        <a-col :span="8">
          <a-form-item
            class="no-margin-and-padding"
            v-bind="errorsBind('first_slot')"
            label="Czas rozpoczęcia"
            required
          >
            <a-select
              v-model="form.first_slot"
              :disabled="!form.selectedDate || isFormDisabled"
              placeholder="Wybierz godzinę"
              :options="slotOptions"
            />
          </a-form-item>
        </a-col>
        <a-col :span="8">
          <a-form-item
            class="no-margin-and-padding"
            v-bind="errorsBind('duration')"
            label="Czas trwania"
            required
          >
            <a-select
              v-model="form.duration"
              :options="durationOptions"
              :disabled="isFormDisabled"
              optionFilterProp="children"
              showSearch
              placeholder="Wybierz czas trwania zajęć"
            />
          </a-form-item>
        </a-col>
      </a-row>
    </a-form-item>
    <a-form-item label="Cykliczne" v-bind="errorsBind('interval')">
      <a-select
        v-model="form.interval"
        :disabled="!form.selectedDate || isFormDisabled"
        :options="intervalOptions"
        @change="intervalChanged"
        placeholder="Wybierz jeśli chcesz dodać rezerwacje cykliczną"
        allowClear
      />
    </a-form-item>

    <a-form-item
      v-if="form.interval"
      label="Do kiedy?"
      v-bind="errorsBind('interval_end')"
      required
    >
      <date-picker
        class="fluid"
        :disabled="!form.selectedDate || isFormDisabled"
        v-model="form.interval_end"
        placeholder="Ostatni dzień"
      />
    </a-form-item>

    <info :course="selectedCourse" :form="form" />
  </a-form>
</template>

<script>
import FieldHelper from '@/components/common/forms/FieldsHelper'
import Info from './Info'
import { Slot } from '@/api'
import moment from 'moment'

export default {
  data() {
    return {
      intervalOptions: [
        { label: 'Codziennie', value: 1 },
        { label: 'Co tydzień', value: 7 },
        { label: 'Co dwa tygodnie', value: 14 }
      ],
      slots: []
    }
  },
  extends: FieldHelper,
  props: {
    courses: {
      type: Array,
      required: true
    },
    isFormDisabled: {
      type: Boolean,
      default: false
    }
  },
  components: {
    Info
  },
  methods: {
    fetchSlots() {
      const params = {
        availability__lecturer: this.form.selectedLecturer,
        is_available: true
      }
      Slot.fetch({}, { params }).then(res => {
        this.slots = res.data.map(slot => ({
          ...slot,
          iso: moment(slot.start).toISOString(),
          value: slot.id,
          label: `${this.getTimeOnly(slot.start)} - ${this.getTimeOnly(
            slot.end
          )} `
        }))
        const uniqueDates = new Set(
          this.slots.map(slot => this.getDateOnly(slot.start))
        )
        this.availableDates = [...uniqueDates]
      })
    },
    dateChanged() {
      this.form.selectedSlot = null
    },
    intervalChanged(value) {
      if (value == null) {
        this.form.interval_end = null
      }
    },
    disabledDate(current) {
      return !this.availableDates.includes(this.getDateOnly(current))
    },
    getLongEnoughSlotsInDay(day) {
      const selectedDate = this.getDateOnly(day)
      const slotInSelectedDay = this.slots.filter(
        slot => this.getDateOnly(slot.start) === selectedDate
      )
      const slots = slotInSelectedDay.map(slot => ({
        label: moment(slot.start).format('HH:mm'),
        value: slot.id,
        start: moment(slot.start)
          .seconds(0)
          .milliseconds(0),
        end: moment(slot.end)
          .seconds(0)
          .milliseconds(0)
      }))

      return this.splitUnContinuousSlots(slots)
        .map(this.filterEarlyEnoughSlots)
        .flat()
    },
    splitUnContinuousSlots(slots) {
      // split array of slots to array of array of continuous slots
      if (slots.length === 0) {
        return []
      }
      const continuousSlots = [[slots[0]]]
      const continuous = (a, b) => {
        return a.end.valueOf() === b.start.valueOf()
      }
      for (let i = 1; i < slots.length; i++) {
        const curr = slots[i]
        const prev = slots[i - 1]
        if (continuous(prev, curr)) {
          continuousSlots[continuousSlots.length - 1].push(curr)
        } else {
          continuousSlots.push([curr])
        }
      }
      return continuousSlots
    },
    filterEarlyEnoughSlots(slots) {
      const lastSlot = slots[slots.length - 1]
      const earlyEnough = slot => {
        return slot.start
          .clone()
          .add(this.form.duration, 'minutes')
          .isSameOrBefore(lastSlot.end)
      }
      return slots.filter(earlyEnough)
    }
  },
  computed: {
    durationOptions() {
      return [
        { label: '45 minut', value: 45 },
        { label: '60 minut', value: 60 },
        { label: '90 minut', value: 90 },
        { label: '120 minut', value: 120 },
      ]
    },
    slotOptions() {
      return this.getLongEnoughSlotsInDay(this.form.selectedDate)
    },
    selectedCourse() {
      return this.courses.find(course => course.id === this.form.course)
    }
  },
  watch: {
    slotOptions(value) {
      if (value.length === 1) {
        this.$set(this.form, 'first_slot', value[0].value)
      }
    },
    'form.selectedLecturer': {
      immediate: true,
      handler() {
        this.fetchSlots()
      }
    }
  }
}
</script>
